import React, { useEffect } from 'react';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { PlaneCut } from '@paragon/contract-library';
import { isOverhangSignal } from '@paragon/layout';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { useScrollTo } from '../../../../../scrolling';
import { highlightUnknownTypePlaneByGuid } from '../../../../../store/actions/layout/genericPlaneActions';
import { editRoofPlaneCutOverhang } from '../../../../../store/actions/layout/roofPlaneActions';
import { getPickingObjectInfo } from '../../../../../store/selectors/pickingSelectors';
import { layoutSlice } from '../../../../../store/slices/layoutSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks';

export function PlaneCutsEditor(props: {
  readonly roofPlaneCuts?: boolean;
  readonly basePlaneGuid: string;
  readonly cuts: ReadonlyArray<PlaneCut>;

  onStartPicking(): void | Promise<void>;
  onFinishPicking(): void | Promise<void>;
  onAutoCut?(): void | Promise<void>;

  readonly disabled?: boolean;
}) {
  const dispatch = useAppDispatch();

  useEffect(() => () => void dispatch(layoutSlice.actions.highlightedPlanesCleared()), [dispatch]);

  const cutButtonScrollTarget = useScrollTo('cutPlaneButton');

  const isPickingCutPlane = useAppSelector((state) => {
    const info = getPickingObjectInfo(state);
    return Boolean(
      info &&
      info.type === 'pickingPlane' &&
      (info.targetInfo.type === 'roof-cut' || info.targetInfo.type === 'ceiling-cut') &&
      info.targetInfo.planeGuidToCut === props.basePlaneGuid,
    );
  });

  const cutModeButtons = (
    <span ref={cutButtonScrollTarget}>
      <Button
        fullWidth
        variant="outlined"
        startIcon={isPickingCutPlane ? <DoneAllIcon /> : <ContentCutIcon />}
        onClick={async () => {
          if (isPickingCutPlane) {
            await props.onFinishPicking();
          } else {
            await props.onStartPicking();
          }
        }}
        disabled={props.disabled}
      >
        {isPickingCutPlane ? 'Finish Cutting' : props.cuts.length > 0 ? 'Re-Cut Plane' : 'Cut Plane'}
      </Button>
    </span>
  );

  if (props.cuts.length === 0) {
    return cutModeButtons;
  }

  const { roofPlaneCuts = false } = props;
  const cuts = preprocessPlaneCutsForOverhangs(props.basePlaneGuid, props.cuts, roofPlaneCuts);

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Cut Against (in this order)
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cut #</TableCell>
              <TableCell>Type</TableCell>
              {roofPlaneCuts && (
                <Tooltip disableInteractive title="If checked, the roof edge formed by this cut will have an overhang.">
                  <TableCell>Overhang</TableCell>
                </Tooltip>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {cuts.map(({ cut, hasOverhang }, i) => (
              <PlaneCutRow
                key={i}
                basePlaneGuid={props.basePlaneGuid}
                cut={cut}
                index={i}
                isRoof={roofPlaneCuts}
                hasOverhang={hasOverhang}
                disabled={props.disabled}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {cutModeButtons}
    </>
  );
}

function PlaneCutRow(props: {
  readonly basePlaneGuid: string;
  readonly cut: PlaneCut;
  readonly index: number;
  readonly isRoof: boolean;
  readonly hasOverhang: boolean;
  readonly disabled?: boolean;
}) {
  const { cut, index, isRoof, hasOverhang } = props;
  const dispatch = useAppDispatch();
  return (
    <TableRow
      hover
      onPointerOver={() => {
        switch (cut.type) {
          case 'AbsoluteVertical':
            dispatch(layoutSlice.actions.highlightedAbsoluteVerticalPlaneSet(cut));
            break;

          case 'AgainstPlane':
            dispatch(highlightUnknownTypePlaneByGuid(cut.cuttingPlaneGuid));
            break;

          default:
            assertExhaustiveSwitch(cut);
        }
      }}
      onPointerOut={() => dispatch(layoutSlice.actions.highlightedPlanesCleared())}
    >
      <TableCell>{index + 1}</TableCell>

      <TableCell>
        {(() => {
          switch (cut.type) {
            case 'AbsoluteVertical':
              return 'Vertical Cut';

            case 'AgainstPlane':
              return 'Other Plane';

            default:
              assertExhaustiveSwitch(cut);
          }
        })()}
      </TableCell>

      {isRoof && (
        <TableCell>
          <Checkbox
            checked={hasOverhang}
            onChange={async ({ target: { checked } }) =>
              await dispatch(editRoofPlaneCutOverhang(props.basePlaneGuid, index, checked))
            }
            disabled={props.disabled}
          />
        </TableCell>
      )}
    </TableRow>
  );
}

function preprocessPlaneCutsForOverhangs(planeToCutGuid: string, planeCuts: readonly PlaneCut[], isRoof: boolean) {
  // Ceiling planes don't have overhangs
  if (!isRoof) return planeCuts.map((cut) => ({ cut, hasOverhang: false }));

  return planeCuts
    .map((planeCut, i) => {
      const nextPlaneCut = planeCuts[i + 1] as (typeof planeCuts)[0] | undefined;
      return isOverhangSignal(planeCut, planeToCutGuid)
        ? null
        : { cut: planeCut, hasOverhang: !!nextPlaneCut && isOverhangSignal(nextPlaneCut, planeToCutGuid) };
    })
    .filter(notNullish);
}
